import cx from 'classnames';
import { Location } from 'history';
import {
    GROUP_MODERATOR_SHORT,
    PB_POST_HAS_BEEN_EDITED,
    POST_DELETED_BECAUSE_reason,
    POST_DELETED_BY_ADMIN,
    POST_DELETED_BY_AUTHOR,
    POST_DELETED_BY_MODERATOR,
    POST_REMOVE_COMPLAINTS,
    SHOW,
    SHOW_FULL_MESSAGE,
} from 'mk/autogenerated/translations/BlogPost.7f18dea62cd68afb2235f1e576fa1242'
import { linebreaksbr, mentionize, upperCaseFirst, urlize } from 'mk/bazaar/common/filters';
import { hashize } from 'mk/bazaar/common/filters/hashize';
import { authorIsModerator, isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import assetUrl from 'mk/common/assetUrl';
import BlogPostMenu from 'mk2/apps/blogs/components/BlogPostMenu';
import BlogPostPhotos from 'mk2/apps/blogs/components/BlogPostPhotos';
import CommentsButton from 'mk2/apps/blogs/components/CommentsButton';
import Comments from 'mk2/apps/blogs/containers/Comments/Comments';
import { BlogPostEntity, CommentEntity, DeletedByRole } from 'mk2/apps/blogs/schemas';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { getGroupHashtagLink } from 'mk2/apps/blogs/utils';
import { groupsGroupUrl, groupsPostDetailUrl } from 'mk2/apps/groups/urls';
import {
    constructGiphy,
    constructInstagram,
    constructVimeo,
    constructYoutube,
    RenderSnippetAs,
} from 'mk2/apps/wiki/utils';
import AnnouncementBox, { BoxType } from 'mk2/components/AnnouncementBox';
import AvatarImg from 'mk2/components/AvatarImg';
import Btn, { BtnSidePadding, BtnType } from 'mk2/components/Btn';
import Clamp from 'mk2/components/Clamp';
import Emojify from 'mk2/components/Emojify';
import Img from 'mk2/components/Img';
import Link from 'mk2/components/Link';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Complaints from 'mk2/containers/Complaints/Complaints';
import Like, { LikeType } from 'mk2/containers/Like/Like';
import { astToHtml, defaultFilterAdditionalData } from 'mk2/helpers/article_ast/filters';
import { cacheLast } from 'mk2/helpers/cache';
import { hasBuggyClamp, UserAgentParsed } from 'mk2/helpers/detects';
import { timeAt } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import { UserEntity } from 'mk2/schemas';
import React from 'react';
import styles from './BlogPost.mscss';

const emptyCounts = {};

interface OwnProps {
    className?: string;
    comments?: CommentEntity[];
    commentsCount: number;
    complaintsCount?: number;
    imageComplaintsCounts?: { [imageId: number]: number };
    groupModerators?: UserEntity[];
    isPreloaded?: boolean;
    location: Location;
    mobile: boolean;
    post: BlogPostEntity;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    showGroup?: boolean;
    showSticky?: boolean;
    showStrip?: boolean;
    userAgent?: UserAgentParsed;
    version?: number;

    onChangeSticky?(postId: number, sticky: boolean);
    onCommentDelete?(commentId: number, key: string);
    onCommentToggleMenuMore?(commentId: number);
    onDelete?(postId: number);
    onGroupLeave?(groupId: number);
    onHideGroupInFeed?(groupId: number);
    onReport?(entityType: string, commentId: number);
    onToggleMenuMore(postId: number);
}

interface State {
    expanded: boolean;
    showComments: boolean;
}

const deletedByToText = (deletedByRole: DeletedByRole): string => {
    return {
        [DeletedByRole.DeletedByAuthor]: POST_DELETED_BY_AUTHOR,
        [DeletedByRole.DeletedByAdmin]: POST_DELETED_BY_ADMIN,
        [DeletedByRole.DeletedByModerator]: POST_DELETED_BY_MODERATOR,
    }[deletedByRole];
};

const PostDateLink = ({ post }: { post: BlogPostEntity }) => {
    const agoText = timeAt(post.created, process.env.NODE_ENV === 'test' ? new Date('2018-11-21T11:19:47.166Z') : null);
    return post.group ? (
        <Link
            className={styles.BlogPost__body__meta__created}
            to={url(groupsPostDetailUrl, { groupId: post.group.id, postSlug: post.slug })}
        >
            {agoText}
        </Link>
    ) : (
        <span className={styles.BlogPost__body__meta__created}>
            {agoText}
        </span>
    );
};

const YOUTUBE_SNIPPET_RE = /youtube#video=([\w-]+)/;
const VIMEO_SNIPPET_RE = /vimeo#video=([\w-]+)/;
const GIPHY_SNIPPET_RE = /giphy#gif=([\w-]+)/;
const INSTAGRAM_SNIPPET_RE = /instagram#post=([\w-]+)/;

const Snippet = ({ snippet }: { snippet: string }) => {
    const youtubeMatcher = new RegExp(YOUTUBE_SNIPPET_RE, 'g');
    const vimeoMatcher = new RegExp(VIMEO_SNIPPET_RE, 'g');
    const giphyMatcher = new RegExp(GIPHY_SNIPPET_RE, 'g');
    const instagramMatcher = new RegExp(INSTAGRAM_SNIPPET_RE, 'g');

    const youtubeMatch = youtubeMatcher.exec(snippet);
    const vimeoMatch = vimeoMatcher.exec(snippet);
    const giphyMatch = giphyMatcher.exec(snippet);
    const instagramMatch = instagramMatcher.exec(snippet);

    let retVal = null;

    if (youtubeMatch && !retVal) {
        const code = youtubeMatch[1];
        retVal = (
            <div className={styles.BlogPost__body__snippet}>
                {constructYoutube(
                    code,
                    620,
                    348,
                )}
            </div>
        );
    }
    if (vimeoMatch && !retVal) {
        const code = vimeoMatch[1];
        retVal = (
            <div className={styles.BlogPost__body__snippet}>
                {constructVimeo(
                    code,
                    620,
                    348,
                )}
            </div>
        );
    }
    if (giphyMatch && !retVal) {
        const code = giphyMatch[1];
        retVal = (
            <div className={styles.BlogPost__body__snippet}>
                {constructGiphy(
                    code,
                    620,
                    348,
                )}
            </div>
        );
    }
    if (instagramMatch && !retVal) {
        const code = instagramMatch[1];
        retVal = (
            <div className={styles.BlogPost__body__snippet}>
                {constructInstagram(
                    code,
                    1040,
                )}
            </div>
        );
    }
    return retVal;
};

export default class BlogPost extends React.PureComponent<OwnProps, State> {

    public static defaultProps: Partial<OwnProps> = {
        showGroup: false,
        showStrip: true,
    };

    private astToHtmlCache = cacheLast();

    private messageFormTextarea: HTMLTextAreaElement;

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            expanded: !!props.isPreloaded,
            showComments: !!props.isPreloaded,
        };
    }

    public render() {
        const {
            className, commentsCount, complaintsCount, groupModerators, imageComplaintsCounts, isPreloaded,
            location, mobile, onCommentDelete, onCommentToggleMenuMore, onChangeSticky, onDelete, onHideGroupInFeed,
            onGroupLeave, onReport, onToggleMenuMore, post, requestUser, requestUserPermissions, showGroup,
            showSticky, showStrip, userAgent,
        } = this.props;

        const avoidClampUse = userAgent ? hasBuggyClamp(userAgent) : false;

        const showReply = true;
        const showLike = true;
        const appearSticky = showSticky && post.sticky;
        const expanded = !appearSticky || this.state.expanded || avoidClampUse;

        const postAuthorIsModerator = groupModerators ? authorIsModerator(post.author, groupModerators) : false;
        const isModerator = groupModerators ? authorIsModerator(requestUser, groupModerators) : false;
        const isGroupsAdmin = requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;
        const isBlogsAdmin = requestUserPermissions.indexOf('photoblog.can_manage_blogs') >= 0;
        const isAuthor = isAuthenticatedAs(requestUser, post.author);

        // prepare post message body
        let bodyChunks: React.ReactChild[] = null;

        if (post.perexAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: post.linkedObjects,
                renderSpecialWikiLinks: false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_LINK,
                hashizeFn: this.getHashizeFn(post.group.id),
            };

            // caching array1 (an empty array), using article(ast) as a cache key. Since array1 is different during each
            // render, the array2 (cached array) and the array1 is equal only once (on first render), and on this
            // occasion we push into the cached array the result of the time expensive function.
            const array1 = [];
            const array2 = this.astToHtmlCache(array1, post.perexAST);
            if (array1 === array2) {
                array2.push(astToHtml(post.perexAST, additionalData));
            }
            bodyChunks = array2[0];
        }

        let role;
        if (isGroupsAdmin || isBlogsAdmin) {
            role = 'admin';
        } else if (isModerator) {
            role = 'moderator';
        } else if (isAuthor) {
            role = 'author';
        }

        return (post.deletedAt && !this.state.expanded) ? (
            <div className={cx(styles.BlogPost, styles['BlogPost--deleted'], className)}>
                <a
                    href={url(blogsProfileUrl, { username: post.author.username})}
                    className={styles.BlogPost__deletedAuthor}
                >
                    {post.author.username}
                </a>

                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {deletedByToText(post.deletedByRole)}
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;

                <Btn
                    key="deleted-btn"
                    onClick={this.handleExpand}
                    label={SHOW}
                    sidePadding={BtnSidePadding.None}
                    type={BtnType.BlueTextLight}
                />
            </div>
        ) : (
            <div
                className={cx(
                    styles.BlogPost,
                    this.state.showComments && styles['BlogPost--commentsOpen'],
                    className,
                )}
            >

                <div className={styles.BlogPost__body}>
                    {expanded && (
                        <div className={styles.BlogPost__body__meta}>
                            <div className={styles.BlogPost__body__meta__avatarcol}>
                                <a href={url(blogsProfileUrl, { username: post.author.username })}>
                                    <AvatarImg
                                        size={36}
                                        user={post.author}
                                        className={styles.BlogPost__body__meta__avatar}
                                    />
                                </a>
                            </div>

                            <div>
                                <a
                                    href={url(blogsProfileUrl, { username: post.author.username })}
                                    className={styles.BlogPost__body__meta__author}
                                >
                                    {post.author.username}
                                </a>
                                {postAuthorIsModerator &&
                                    <span className={styles.BlogPost__body__meta__title}>{GROUP_MODERATOR_SHORT}</span>
                                }
                                {showGroup && post.group && (
                                    <span className={styles.BlogPost__body__meta__group}>
                                        › <Link
                                            className={styles.BlogPost__body__meta__group__name}
                                            to={url(groupsGroupUrl, { groupId: post.group.id })}
                                        >
                                            {post.group.name}
                                        </Link>
                                    </span>
                                )}
                                <div>
                                    {post.wasEdited && (
                                        <span className={styles.BlogPost__body__meta__created}>
                                        {PB_POST_HAS_BEEN_EDITED}
                                            <span className={styles.BlogPost__body__meta__sep}>•</span>
                                    </span>
                                    )}
                                    <PostDateLink post={post} />
                                </div>
                            </div>

                            {expanded && (
                                <BlogPostMenu
                                    className={styles.BlogPost__body__meta__dropdownMenu}
                                    post={post}
                                    requestUser={requestUser}
                                    location={location}
                                    hasDelete
                                    hasEdit
                                    hasLeave={showGroup}
                                    hasHideGroupInFeed={showGroup}
                                    hasReport
                                    isAdmin={isBlogsAdmin || isGroupsAdmin}
                                    isAuthor={isAuthor}
                                    isModerator={isModerator}
                                    onChangeSticky={onChangeSticky}
                                    onDelete={onDelete}
                                    onGroupLeave={onGroupLeave}
                                    onHideGroupInFeed={onHideGroupInFeed}
                                    onReport={onReport}
                                    onToggleMenuMore={onToggleMenuMore}
                                />
                            )}
                        </div>
                    )}

                    <div className={styles.BlogPost__body__content}>
                        <div className={styles.BlogPost__body__content__text}>
                            <div>
                                {post.deletedAt && (
                                    <AnnouncementBox
                                        message={deletedByToText(post.deletedByRole)}
                                        deletedBy={post.deletedBy ? post.deletedBy.username : null}
                                        detailMessage={post.deletedReason ? post.deletedReason : null}
                                        deletedAt={post.deletedAt}
                                        hasDetailMessage={!!post.deletedReason}
                                        icon={'fa fa-trash'}
                                        reasonValue={POST_DELETED_BECAUSE_reason}
                                        type={BoxType.Deleted}
                                    />
                                )}

                                {(appearSticky && !expanded) ? (
                                    !!bodyChunks && (
                                        <div className={styles.BlogPost__body__content__text__perex}>
                                            <Clamp clamp={3}>
                                                <div>
                                                    <i className={cx(styles.BlogPost__body__content__stickyFa, 'fa fa-thumb-tack')} />

                                                    <Emojify size={17}>
                                                        {bodyChunks}
                                                    </Emojify>
                                                </div>
                                            </Clamp>
                                            <div className={cx(styles.BlogPost__body__content__expand)} onClick={this.handleExpand}>
                                                {SHOW_FULL_MESSAGE}
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    !!bodyChunks && (
                                        <div className={styles.BlogPost__body__content__text__perex}>
                                            {appearSticky && (
                                                <i className={cx(styles.BlogPost__body__content__stickyFa, 'fa fa-thumb-tack')} />
                                            )}

                                            <Emojify size={17}>
                                                {bodyChunks}
                                            </Emojify>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        {expanded && post.snippet && (
                            <Snippet snippet={post.snippet} />
                        )}

                        {expanded && post.stickerId && (
                            <div>
                                <Img
                                    lazy
                                    src={assetUrl + 'img/photoblog/stickers/' + post.stickerId + '.png'}
                                    width={100}
                                    height={100}
                                />
                            </div>
                        )}

                        {expanded && (
                            <BlogPostPhotos
                                inFeed={!isPreloaded}
                                complaintsCounts={imageComplaintsCounts || emptyCounts}
                                mobile={mobile}
                                post={post}
                                showStrip={showStrip}
                            />
                        )}
                    </div>
                </div>

                {(role === 'admin' || role === 'moderator') && (
                    <Complaints
                        className={styles.BlogPost__complaints}
                        id={post.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                    />
                )}

                {expanded && (
                    <div className={styles.BlogPost__footer}>
                        <div className={styles.BlogPost__footer__likes}>
                            {showLike && (
                                <Like
                                    entity="post"
                                    id={post.id}
                                    type={LikeType.IconCount}
                                    className={styles.BlogPost__footer__likes__like}
                                />
                            )}
                        </div>
                        <div className={styles.BlogPost__footer__comments}>
                            {showReply && (
                                <CommentsButton
                                    commentsCount={commentsCount}
                                    className={cx(
                                        styles.BlogPost__footer__comments__commentsButton,
                                        complaintsCount && styles['BlogPost__footer__comments__commentsButton--has-complaints'],
                                    )}
                                    onClick={this.handleOnComments}
                                />
                            )}
                        </div>
                    </div>
                )}

                {this.state.showComments && (
                    <Comments
                        postKey={`message/${post.id}`}
                        count={commentsCount}
                        inGroups
                        inPrivateGroup={post.group && post.group.locked}
                        location={location}
                        moderators={groupModerators}
                        onCommentDelete={onCommentDelete}
                        onToggleMenuMore={onCommentToggleMenuMore}
                        hasBlueToolbar={isPreloaded}
                        onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                        post={post}
                    />
                )}
            </div>
        );
    }

    private handleExpand = () => {
        this.setState({ expanded: true });
    };

    private handleOnComments = () => {
        if (this.props.isPreloaded) {
            if (this.messageFormTextarea) {
                this.messageFormTextarea.focus();
            }
        } else {
            this.setState((state) => {
                return {
                    showComments: !state.showComments,
                };
            });
        }
    };

    private handleSetMessageFormTextareaRef = (ref: any) => {
        this.messageFormTextarea = ref;
    };

    private getHashizeFn(postGroupId: number) {
        return (bodyChunks) => hashize(
            bodyChunks,
            (hash) => getGroupHashtagLink(postGroupId, hash),
            true,
        );
    }
}
